// PAIA Manual content for SureDox
// Last updated: January 2025

export const paiaManualContent = `
<h1>PAIA Manual</h1>

<h2>Manual in Terms of Section 51 of the Promotion of Access to Information Act, 2000 (Act No. 2 of 2000)</h2>

<p><strong>Boone and Boo (Pty) Ltd</strong> (trading as SureDox)</p>

<hr />

<h2>1. Introduction</h2>

<p>This Manual is published in compliance with Section 51 of the Promotion of Access to Information Act, 2000 (Act No. 2 of 2000) ("PAIA") and Section 18 of the Protection of Personal Information Act, 2013 (Act No. 4 of 2013) ("POPIA").</p>

<p>The purpose of PAIA is to give effect to the constitutional right of access to information held by private bodies that is required for the exercise or protection of any rights.</p>

<p>This Manual:</p>

<ul>
<li>Provides information about Boone and Boo (Pty) Ltd and the records we hold</li>
<li>Explains how to request access to records</li>
<li>Describes the categories of information we hold</li>
<li>Sets out the grounds on which access may be refused</li>
<li>Provides contact details for requests</li>
</ul>

<hr />

<h2>2. Contact Details</h2>

<h3>2.1 Head of Private Body</h3>

<p><strong>Name:</strong> Eben Smit<br />
<strong>Designation:</strong> Managing Director<br />
<strong>Physical Address:</strong> 9 Clarence Avenue, Craighall Park, Johannesburg<br />
<strong>Email:</strong> <a href="mailto:eben@suredox.co.za">eben@suredox.co.za</a></p>

<h3>2.2 Information Officer (Section 55 of POPIA)</h3>

<p><strong>Name:</strong> Eben Smit<br />
<strong>Designation:</strong> Information Officer<br />
<strong>Physical Address:</strong> 9 Clarence Avenue, Craighall Park, Johannesburg<br />
<strong>Email:</strong> <a href="mailto:eben@suredox.co.za">eben@suredox.co.za</a></p>

<h3>2.3 Deputy Information Officer(s)</h3>

<p>None appointed</p>

<hr />

<h2>3. The South African Human Rights Commission</h2>

<p>The South African Human Rights Commission (SAHRC) has compiled a guide on how to use PAIA. This guide is available from the SAHRC.</p>

<p><strong>Contact details:</strong></p>

<p>PAIA Unit<br />
The South African Human Rights Commission<br />
Private Bag X2700<br />
Houghton<br />
2041</p>

<p><strong>Telephone:</strong> 011 877 3600<br />
<strong>Website:</strong> <a href="https://www.sahrc.org.za" target="_blank" rel="noopener noreferrer">www.sahrc.org.za</a><br />
<strong>Email:</strong> <a href="mailto:paia@sahrc.org.za">paia@sahrc.org.za</a></p>

<hr />

<h2>4. Information Regulator</h2>

<p>Complaints regarding POPIA compliance can be directed to:</p>

<p><strong>The Information Regulator (South Africa)</strong><br />
JD House<br />
27 Stiemens Street<br />
Braamfontein<br />
Johannesburg<br />
2001</p>

<p><strong>P.O. Box:</strong> 31533, Braamfontein, Johannesburg, 2017<br />
<strong>Telephone:</strong> 010 023 5200<br />
<strong>Email:</strong> <a href="mailto:inforeg@justice.gov.za">inforeg@justice.gov.za</a><br />
<strong>Website:</strong> <a href="https://www.justice.gov.za/inforeg" target="_blank" rel="noopener noreferrer">www.justice.gov.za/inforeg</a></p>

<hr />

<h2>5. Description of Boone and Boo (Pty) Ltd</h2>

<h3>5.1 Overview</h3>

<p>Boone and Boo (Pty) Ltd, trading as SureDox, is a South African company providing software-as-a-service (SaaS) solutions for document processing. Our primary service is an AI-powered document redaction platform that helps organisations comply with POPIA by automatically detecting and redacting personally identifiable information from documents.</p>

<h3>5.2 Business Activities</h3>

<ul>
<li>Document redaction software services</li>
<li>AI-powered PII (Personal Identifiable Information) detection</li>
<li>POPIA compliance tools for businesses</li>
</ul>

<h3>5.3 Registration Details</h3>

<p><strong>Company Registration Number:</strong> 2025 / 489700 / 07<br />
<strong>Registered Address:</strong> 9 Clarence Avenue, Craighall Park, Johannesburg</p>

<hr />

<h2>6. Guide on How to Use PAIA and How to Obtain Access to the Guide</h2>

<h3>6.1 SAHRC Guide</h3>

<p>The SAHRC has, in terms of Section 10 of PAIA, compiled a guide containing information to assist persons wishing to exercise their rights under PAIA. The guide is available in all official languages and can be obtained from:</p>

<ul>
<li>The SAHRC website: <a href="https://www.sahrc.org.za" target="_blank" rel="noopener noreferrer">www.sahrc.org.za</a></li>
<li>By contacting the SAHRC at the details in Section 3 above</li>
<li>By requesting a copy from our Information Officer</li>
</ul>

<h3>6.2 Using This Manual</h3>

<p>This Manual explains what records SureDox holds, how to request access, the prescribed fees, and the grounds on which access may be refused.</p>

<hr />

<h2>7. Categories of Records Held by SureDox</h2>

<h3>7.1 Records Available Without a Request</h3>

<p>The following information is publicly available on our website (<a href="https://www.suredox.co.za" target="_blank" rel="noopener noreferrer">www.suredox.co.za</a>):</p>

<ul>
<li>Service descriptions and pricing</li>
<li>Privacy Policy</li>
<li>Terms of Service</li>
<li>This PAIA Manual</li>
<li>Contact information</li>
</ul>

<h3>7.2 Records Available on Request</h3>

<p>Records are organised into the following categories:</p>

<h4>A. Company Records</h4>

<table>
<thead>
<tr><th>Category</th><th>Description</th></tr>
</thead>
<tbody>
<tr><td>Incorporation documents</td><td>Memorandum of Incorporation, registration certificates</td></tr>
<tr><td>Statutory records</td><td>Annual returns, director registers</td></tr>
<tr><td>Minutes</td><td>Board and shareholder meeting minutes</td></tr>
<tr><td>Contracts</td><td>Agreements with suppliers, partners, customers</td></tr>
<tr><td>Policies</td><td>Internal policies and procedures</td></tr>
</tbody>
</table>

<h4>B. Financial Records</h4>

<table>
<thead>
<tr><th>Category</th><th>Description</th></tr>
</thead>
<tbody>
<tr><td>Annual financial statements</td><td>Audited or reviewed financial statements</td></tr>
<tr><td>Tax records</td><td>Income tax records</td></tr>
<tr><td>Banking records</td><td>Bank statements, payment records</td></tr>
<tr><td>Invoices</td><td>Customer and supplier invoices</td></tr>
<tr><td>Credit records</td><td>Credit applications and related documents</td></tr>
</tbody>
</table>

<h4>C. Human Resources Records</h4>

<table>
<thead>
<tr><th>Category</th><th>Description</th></tr>
</thead>
<tbody>
<tr><td>Employment contracts</td><td>Agreements with employees</td></tr>
<tr><td>Payroll records</td><td>Salary, PAYE, UIF records</td></tr>
<tr><td>Leave records</td><td>Employee leave applications and balances</td></tr>
<tr><td>Performance records</td><td>Performance reviews, disciplinary records</td></tr>
<tr><td>Training records</td><td>Employee training and development</td></tr>
</tbody>
</table>

<h4>D. Customer and Operational Records</h4>

<table>
<thead>
<tr><th>Category</th><th>Description</th></tr>
</thead>
<tbody>
<tr><td>Customer accounts</td><td>Account information, contact details</td></tr>
<tr><td>Transaction records</td><td>Credit purchases, usage history</td></tr>
<tr><td>Support records</td><td>Customer support tickets and correspondence</td></tr>
<tr><td>Audit logs</td><td>System access and activity logs</td></tr>
</tbody>
</table>

<h4>E. Technical and IT Records</h4>

<table>
<thead>
<tr><th>Category</th><th>Description</th></tr>
</thead>
<tbody>
<tr><td>System documentation</td><td>Technical specifications, architecture documents</td></tr>
<tr><td>Security records</td><td>Security assessments, incident reports</td></tr>
<tr><td>Backup records</td><td>Data backup logs and procedures</td></tr>
</tbody>
</table>

<h4>F. Legal and Compliance Records</h4>

<table>
<thead>
<tr><th>Category</th><th>Description</th></tr>
</thead>
<tbody>
<tr><td>Regulatory correspondence</td><td>Communications with regulators</td></tr>
<tr><td>Legal opinions</td><td>Legal advice received</td></tr>
<tr><td>Compliance documentation</td><td>POPIA compliance records, certifications</td></tr>
</tbody>
</table>

<h3>7.3 Records Held in Terms of Other Legislation</h3>

<p>SureDox holds records in compliance with the following legislation (non-exhaustive):</p>

<ul>
<li>Companies Act, 2008 (Act No. 71 of 2008)</li>
<li>Income Tax Act, 1962 (Act No. 58 of 1962)</li>
<li>Basic Conditions of Employment Act, 1997 (Act No. 75 of 1997)</li>
<li>Labour Relations Act, 1995 (Act No. 66 of 1995)</li>
<li>Employment Equity Act, 1998 (Act No. 55 of 1998)</li>
<li>Unemployment Insurance Act, 2001 (Act No. 63 of 2001)</li>
<li>Skills Development Levies Act, 1999 (Act No. 9 of 1999)</li>
<li>Electronic Communications and Transactions Act, 2002 (Act No. 25 of 2002)</li>
<li>Protection of Personal Information Act, 2013 (Act No. 4 of 2013)</li>
<li>Consumer Protection Act, 2008 (Act No. 68 of 2008)</li>
</ul>

<hr />

<h2>8. Processing of Personal Information</h2>

<h3>8.1 Purpose of Processing</h3>

<p>SureDox processes personal information for the following purposes:</p>

<table>
<thead>
<tr><th>Category of Data Subject</th><th>Purpose of Processing</th></tr>
</thead>
<tbody>
<tr><td>Customers/Users</td><td>Account management, service delivery, billing, support, marketing (with consent)</td></tr>
<tr><td>Employees</td><td>Employment relationship, payroll, statutory compliance</td></tr>
<tr><td>Suppliers/Service Providers</td><td>Contract management, payment processing</td></tr>
<tr><td>Website Visitors</td><td>Analytics, security, service improvement</td></tr>
</tbody>
</table>

<h3>8.2 Categories of Personal Information Processed</h3>

<table>
<thead>
<tr><th>Category</th><th>Types of Information</th></tr>
</thead>
<tbody>
<tr><td>Identity information</td><td>Name, ID number, registration numbers</td></tr>
<tr><td>Contact information</td><td>Email, phone, physical address</td></tr>
<tr><td>Financial information</td><td>Banking details, payment history</td></tr>
<tr><td>Technical information</td><td>IP address, device information, usage data</td></tr>
<tr><td>Employment information</td><td>For employees: qualifications, employment history</td></tr>
</tbody>
</table>

<h3>8.3 Categories of Data Subjects</h3>

<ul>
<li>Customers and registered users of the SureDox platform</li>
<li>Employees and contractors</li>
<li>Suppliers and service providers</li>
<li>Website visitors</li>
<li>Prospective customers</li>
</ul>

<h3>8.4 Recipients of Personal Information</h3>

<p>Personal information may be shared with:</p>

<ul>
<li>Service providers (hosting, payment processing, AI processing)</li>
<li>Professional advisors (accountants, lawyers)</li>
<li>Regulatory authorities (as required by law)</li>
<li>Affiliated companies (if applicable)</li>
</ul>

<p>A list of our sub-processors is available in our <a href="/privacy">Privacy Policy</a> and <a href="/terms">Terms of Service</a>.</p>

<h3>8.5 Cross-Border Transfers</h3>

<p>Personal information may be transferred outside South Africa to our service providers located in other countries. Such transfers are made in compliance with Section 72 of POPIA.</p>

<h3>8.6 Security Measures</h3>

<p>We implement appropriate technical and organisational measures to protect personal information, including encryption, access controls, and regular security assessments. Details are available in our Security Safeguards documentation.</p>

<h3>8.7 Objection to Processing for Direct Marketing</h3>

<p>You have the right to object to the processing of your personal information for direct marketing purposes. To exercise this right, contact our Information Officer.</p>

<h3>8.8 Right to Lodge a Complaint</h3>

<p>You have the right to lodge a complaint with the Information Regulator if you believe your personal information has been processed in violation of POPIA. Contact details for the Information Regulator are provided in Section 4.</p>

<hr />

<h2>9. Request Procedure</h2>

<h3>9.1 Form of Request</h3>

<p>A request for access to a record must be made on the prescribed form (Form C), available from:</p>

<ul>
<li>The Department of Justice website: <a href="https://www.justice.gov.za" target="_blank" rel="noopener noreferrer">www.justice.gov.za</a></li>
<li>Our Information Officer (on request)</li>
</ul>

<h3>9.2 Submitting a Request</h3>

<p>Completed request forms should be submitted to:</p>

<p><strong>Information Officer</strong><br />
Boone and Boo (Pty) Ltd<br />
9 Clarence Avenue, Craighall Park, Johannesburg</p>

<p><strong>Or by email:</strong> <a href="mailto:eben@suredox.co.za">eben@suredox.co.za</a></p>

<h3>9.3 Information Required</h3>

<p>The request form requires:</p>

<ul>
<li>Your contact details</li>
<li>Sufficient detail to identify the record(s) requested</li>
<li>The form of access required (e.g., copy, inspection)</li>
<li>Proof of identity (certified copy of ID document)</li>
<li>If requesting on behalf of another person, proof of authority</li>
</ul>

<h3>9.4 Response Time</h3>

<p>We will respond to your request within 30 days of receipt. This period may be extended by a further 30 days if:</p>

<ul>
<li>The request is for a large number of records</li>
<li>Searching for the records requires a search through a large volume of information</li>
<li>Consultation with another party is required</li>
</ul>

<p>You will be notified of any extension.</p>

<hr />

<h2>10. Fees</h2>

<h3>10.1 Request Fee</h3>

<p>A non-refundable request fee of R50.00 is payable when submitting a request. This fee may be waived for personal requesters (individuals requesting access to their own personal information).</p>

<h3>10.2 Access Fee</h3>

<p>If the request is granted, an access fee may be payable before access is provided. The access fee depends on the form of access and the volume of records. A deposit may be required.</p>

<h3>10.3 Fee Schedule</h3>

<table>
<thead>
<tr><th>Item</th><th>Fee</th></tr>
</thead>
<tbody>
<tr><td>Request fee</td><td>R50.00</td></tr>
<tr><td>Photocopy (per page, A4)</td><td>R1.10</td></tr>
<tr><td>Printed copy (per page, A4)</td><td>R0.75</td></tr>
<tr><td>Copy on computer-readable format (per CD)</td><td>R70.00</td></tr>
<tr><td>Transcription (per page, A4)</td><td>R40.00</td></tr>
<tr><td>Preparation fee (per hour or part thereof)</td><td>R145.00</td></tr>
<tr><td>Postage</td><td>Actual cost</td></tr>
</tbody>
</table>

<p><em>Fees are subject to change in accordance with regulations.</em></p>

<h3>10.4 Payment</h3>

<p>Fees are payable by electronic funds transfer (EFT).</p>

<hr />

<h2>11. Grounds for Refusal of Access</h2>

<p>Access to records may be refused on the following grounds as set out in Chapter 4 of PAIA:</p>

<h3>11.1 Mandatory Grounds for Refusal</h3>

<ul>
<li><strong>Section 63:</strong> Privacy of a third party (natural person)</li>
<li><strong>Section 64:</strong> Commercial information of a third party</li>
<li><strong>Section 65:</strong> Confidential information of a third party</li>
<li><strong>Section 66:</strong> Safety of individuals and protection of property</li>
<li><strong>Section 67:</strong> Police dockets, judicial functions, and legal privilege</li>
<li><strong>Section 68:</strong> Defence, security, and international relations</li>
<li><strong>Section 69:</strong> Economic interests and financial welfare of the Republic</li>
<li><strong>Section 70:</strong> Operations of public bodies</li>
</ul>

<h3>11.2 Discretionary Grounds for Refusal</h3>

<ul>
<li><strong>Section 68:</strong> Manifestly frivolous or vexatious requests</li>
<li><strong>Section 69:</strong> Resources required would substantially and unreasonably divert resources</li>
</ul>

<h3>11.3 Third Party Information</h3>

<p>Where a request involves information relating to a third party, we may be required to notify that third party and consider their representations before deciding on the request.</p>

<hr />

<h2>12. Remedies</h2>

<h3>12.1 Internal Remedies</h3>

<p>If your request is refused, you may lodge an internal appeal by writing to the head of SureDox within 60 days of the decision.</p>

<h3>12.2 External Remedies</h3>

<p>If you are not satisfied with the outcome of an internal appeal, you may:</p>

<ul>
<li>Apply to a court for appropriate relief</li>
<li>Lodge a complaint with the Information Regulator (for POPIA-related matters)</li>
</ul>

<hr />

<h2>13. Availability of This Manual</h2>

<p>This Manual is available:</p>

<ul>
<li>On our website: <a href="https://www.suredox.co.za/paia" target="_blank" rel="noopener noreferrer">www.suredox.co.za/paia</a></li>
<li>At our offices during business hours</li>
<li>From the Information Officer on request</li>
<li>From the South African Human Rights Commission</li>
</ul>

<hr />

<h2>14. Updates to This Manual</h2>

<p>This Manual will be updated from time to time as required. The current version is always available on our website.</p>

<p><strong>Version:</strong> 1.0<br />
<strong>Effective Date:</strong> 27 January 2025<br />
<strong>Last Reviewed:</strong> 27 January 2025</p>

<hr />

<h2>Approval</h2>

<p>This Manual was approved by:</p>

<p><strong>Name:</strong> Eben Smit<br />
<strong>Designation:</strong> Managing Director<br />
<strong>Date:</strong> 27 January 2025</p>
`;

export default paiaManualContent;
